<template>
    <div class="tbv2-wrap">
        <div class="tbv2">
            <!-- Left: page title / breadcrumb portal -->
            <div class="tbv2-left">
                <portal-target name="ariane" />
            </div>

            <!-- Right: search + actions + profile -->
            <div v-if="canSearch" class="tbv2-search-wrap" v-click-outside="closeResults">
                <div class="tbv2-search" :class="{ 'is-focused': searchFocused }">
                    <i class="fal fa-search tbv2-search-icon"></i>
                    <input
                        ref="searchInput"
                        v-model="searchQuery"
                        type="text"
                        :placeholder="$t('topbar.search') || 'Rechercher...'"
                        class="tbv2-search-input"
                        @focus="onFocus"
                        @blur="onBlur"
                        @input="onInput"
                        @keydown.down.prevent="moveSelection(1)"
                        @keydown.up.prevent="moveSelection(-1)"
                        @keydown.enter.prevent="goToFirstResult"
                        @keydown.esc="closeResults"
                    />
                    <kbd v-if="!searchFocused && !searchQuery" class="tbv2-search-kbd">{{ shortcutLabel }}</kbd>
                    <i v-if="searching" class="fal fa-spinner-third fa-spin tbv2-search-loader"></i>
                </div>

                <!-- Results -->
                <div v-if="showResults" class="tbv2-results">
                    <div v-if="resultPosts.length" class="tbv2-results-group">
                        <div class="tbv2-results-group-title">{{ $t('topbar.search_posts') || 'Tickets' }}</div>
                        <router-link
                            v-for="(p, i) in resultPosts"
                            :key="'p-' + p.id"
                            :to="`/posts/${p.id}`"
                            class="tbv2-result"
                            :class="{ 'is-selected': selectedIndex === getGlobalIndex('posts', i) }"
                            @click.native="afterPick"
                        >
                            <i class="fal fa-ticket tbv2-result-icon"></i>
                            <span class="tbv2-result-label">#{{ p.reference }} — {{ p.title || '—' }}</span>
                        </router-link>
                    </div>

                    <div v-if="resultForms.length" class="tbv2-results-group">
                        <div class="tbv2-results-group-title">{{ $t('topbar.search_forms') || 'Formulaires' }}</div>
                        <router-link
                            v-for="(f, i) in resultForms"
                            :key="'f-' + f.id"
                            :to="`/forms/${f.id}`"
                            class="tbv2-result"
                            :class="{ 'is-selected': selectedIndex === getGlobalIndex('forms', i) }"
                            @click.native="afterPick"
                        >
                            <i class="fal fa-poll tbv2-result-icon"></i>
                            <span class="tbv2-result-label">{{ f.name }}</span>
                        </router-link>
                    </div>

                    <div v-if="resultUsers.length" class="tbv2-results-group">
                        <div class="tbv2-results-group-title">{{ $t('topbar.search_users') || 'Utilisateurs' }}</div>
                        <router-link
                            v-for="(u, i) in resultUsers"
                            :key="'u-' + u.id"
                            :to="`/company/user/${u.id}/profile`"
                            class="tbv2-result"
                            :class="{ 'is-selected': selectedIndex === getGlobalIndex('users', i) }"
                            @click.native="afterPick"
                        >
                            <i class="fal fa-user tbv2-result-icon"></i>
                            <span class="tbv2-result-label">{{ u.name }}</span>
                            <span v-if="u.email" class="tbv2-result-meta">{{ u.email }}</span>
                        </router-link>
                    </div>

                    <div
                        v-if="!searching && !resultPosts.length && !resultForms.length && !resultUsers.length"
                        class="tbv2-results-empty"
                    >
                        <i class="fal fa-search-minus"></i>
                        {{ $t('topbar.no_results') || 'Aucun résultat' }}
                    </div>
                </div>
            </div>

            <!-- Right: actions + profile -->
            <div class="tbv2-right">
                <slot name="actions" />

                <NotificationsBellV2 ref="notifications" />

                <div class="tbv2-profile" v-if="user" v-click-outside="closeProfile">
                    <button
                        type="button"
                        class="tbv2-profile-trigger"
                        :title="user.email"
                        :aria-expanded="profileOpen ? 'true' : 'false'"
                        @click="profileOpen = !profileOpen"
                    >
                        <img v-if="user.profile_picture" :src="user.profile_picture" :alt="user.name" class="tbv2-profile-img" />
                        <span v-else class="tbv2-profile-avatar">{{ getInitials(user.name) }}</span>
                    </button>

                    <div v-if="profileOpen" class="tbv2-profile-menu" role="menu">
                        <div class="tbv2-profile-header">
                            <img v-if="user.profile_picture" :src="user.profile_picture" :alt="user.name" class="tbv2-profile-header-img" />
                            <span v-else class="tbv2-profile-header-avatar">{{ getInitials(user.name) }}</span>
                            <div class="tbv2-profile-header-info">
                                <span class="tbv2-profile-header-name">{{ user.name }}</span>
                                <span class="tbv2-profile-header-email">{{ user.email }}</span>
                            </div>
                        </div>

                        <div class="tbv2-profile-divider"></div>

                        <router-link to="/profile" class="tbv2-profile-item" @click.native="profileOpen = false">
                            <i class="fal fa-user-circle"></i>
                            <span>{{ $t('my_profile.title') || 'Mon profil' }}</span>
                        </router-link>

                        <div class="tbv2-profile-divider"></div>

                        <div class="tbv2-profile-lang">
                            <span class="tbv2-profile-lang-label">{{ $t('topbar.profil.language') || 'Langue' }}</span>
                            <div class="tbv2-profile-lang-options">
                                <button
                                    v-for="code in ['fr', 'en']"
                                    :key="code"
                                    type="button"
                                    class="tbv2-lang-btn"
                                    :class="{ 'is-active': lang === code }"
                                    @click="changeLang(code)"
                                >
                                    {{ code.toUpperCase() }}
                                </button>
                            </div>
                        </div>

                        <div class="tbv2-profile-divider"></div>

                        <button
                            type="button"
                            class="tbv2-profile-item tbv2-profile-item--logout"
                            @click="doLogout"
                        >
                            <i class="fal fa-sign-out"></i>
                            <span>{{ $t('topbar.profil.logout') || 'Se déconnecter' }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import axios from 'axios'
import { mapActions, mapState } from 'vuex'
import NotificationsBellV2 from '@/components/Layout/v2/NotificationsBellV2.vue'

export default {
    name: 'TopBarV2',
    components: { NotificationsBellV2 },
    directives: {
        'click-outside': {
            bind(el, binding) {
                el._clickOutside = (e) => {
                    if (!el.contains(e.target)) binding.value()
                }
                document.addEventListener('mousedown', el._clickOutside)
            },
            unbind(el) {
                document.removeEventListener('mousedown', el._clickOutside)
            }
        }
    },
    data() {
        return {
            searchQuery: '',
            searchFocused: false,
            searching: false,
            showResults: false,
            selectedIndex: -1,
            debounceTimer: null,
            resultPosts: [],
            resultForms: [],
            resultUsers: [],
            profileOpen: false
        }
    },
    computed: {
        ...mapState({
            baseURL: (s) => s.baseURL,
            lang: (s) => s.lang
        }),
        ...mapState('auth', { user: (s) => s.user }),
        ...mapState('form', { storeForms: (s) => s.forms }),
        permissions() {
            return this.$store.state.auth.permissions
                || JSON.parse(localStorage.getItem('permissions') || '[]')
        },
        canSearchPosts() {
            return this.permissions.includes('posts.view')
        },
        canSearchUsers() {
            return this.permissions.includes('users.view')
        },
        canSearchForms() {
            return this.permissions.includes('forms.view')
        },
        /**
         * Un champ de recherche qui ne peut rien chercher est un piège : on le
         * masque plutôt que de laisser l'utilisateur taper dans le vide.
         */
        canSearch() {
            return this.canSearchPosts || this.canSearchUsers || this.canSearchForms
        },
        allResults() {
            return [
                ...this.resultPosts.map((p) => ({ type: 'post', item: p })),
                ...this.resultForms.map((f) => ({ type: 'form', item: f }))
            ]
        },
        isMac() {
            if (typeof navigator === 'undefined') return false
            const uaData = navigator.userAgentData && navigator.userAgentData.platform
            if (uaData) return /mac|ios/i.test(uaData)
            return /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent || '')
        },
        shortcutLabel() {
            return this.isMac ? '⌘K' : 'Ctrl+K'
        }
    },
    mounted() {
        document.addEventListener('keydown', this.onGlobalKey)
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.onGlobalKey)
        clearTimeout(this.debounceTimer)
    },
    methods: {
        ...mapActions('auth', ['logout']),
        onFocus() {
            this.searchFocused = true
            if (this.searchQuery.length >= 3) this.showResults = true
        },
        onBlur() {
            this.searchFocused = false
        },
        onInput() {
            clearTimeout(this.debounceTimer)
            this.selectedIndex = -1
            if (this.searchQuery.trim().length < 3) {
                this.showResults = false
                this.resultPosts = []
                this.resultForms = []
                this.resultUsers = []
                return
            }
            this.debounceTimer = setTimeout(() => this.doSearch(), 400)
        },
        /**
         * Un appel par droit accordé, et chacun tombe pour son propre compte :
         * un `Promise.all` faisait perdre les deux listes dès qu'une seule était
         * refusée, et le 403 non silencé sortait l'utilisateur de sa page —
         * depuis la barre du haut, donc depuis n'importe où.
         */
        async doSearch() {
            const q = this.searchQuery.trim()
            if (q.length < 3 || !this.canSearch) return
            this.searching = true
            this.showResults = true

            const config = { silence403: true }
            const [postsRes, usersRes] = await Promise.all([
                this.canSearchPosts
                    ? axios
                        .get(`${this.baseURL}/posts`, {
                            ...config,
                            params: { search: q, per_page: 5 }
                        })
                        .catch(() => null)
                    : Promise.resolve(null),
                this.canSearchUsers
                    ? axios
                        .get(`${this.baseURL}/users/find`, {
                            ...config,
                            params: { search: q }
                        })
                        .catch(() => null)
                    : Promise.resolve(null)
            ])

            const posts = postsRes && postsRes.data && postsRes.data.posts
            this.resultPosts = ((posts && posts.data) || []).slice(0, 5)

            const users = usersRes && usersRes.data && usersRes.data.users
            const userList = Array.isArray(users) ? users : (users && users.data) || []
            this.resultUsers = userList.slice(0, 5)

            if (this.canSearchForms && this.storeForms && this.storeForms.length) {
                const lq = q.toLowerCase()
                this.resultForms = this.storeForms
                    .filter((f) => (f.name || '').toLowerCase().includes(lq))
                    .slice(0, 5)
            } else {
                this.resultForms = []
            }

            this.searching = false
        },
        closeResults() {
            this.showResults = false
            this.selectedIndex = -1
        },
        afterPick() {
            this.searchQuery = ''
            this.closeResults()
        },
        getGlobalIndex(type, i) {
            if (type === 'posts') return i
            if (type === 'forms') return this.resultPosts.length + i
            return this.resultPosts.length + this.resultForms.length + i
        },
        moveSelection(dir) {
            const total = this.allResults.length
            if (total === 0) return
            this.selectedIndex = (this.selectedIndex + dir + total) % total
        },
        goToFirstResult() {
            if (this.selectedIndex >= 0 && this.allResults[this.selectedIndex]) {
                const r = this.allResults[this.selectedIndex]
                if (r.type === 'post') this.$router.push(`/posts/${r.item.id}`).catch(() => {})
                else if (r.type === 'form') this.$router.push(`/forms/${r.item.id}`).catch(() => {})
            } else if (this.resultPosts.length) {
                this.$router.push(`/posts/${this.resultPosts[0].id}`).catch(() => {})
            }
            this.afterPick()
        },
        onGlobalKey(e) {
            // Sans champ de recherche rendu, les raccourcis n'ont rien à cibler.
            if (!this.canSearch) return
            const isInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable
            // Cmd+K / Ctrl+K from anywhere, including other inputs
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault()
                if (this.$refs.searchInput) this.$refs.searchInput.focus()
                return
            }
            // Plain "/" only when not already inside a text field
            if (e.key === '/' && !isInput) {
                e.preventDefault()
                if (this.$refs.searchInput) this.$refs.searchInput.focus()
            }
        },
        getInitials(name) {
            if (!name) return '?'
            const parts = name.split(/\s+/).filter(Boolean)
            return ((parts[0] || '').charAt(0) + (parts[1] || '').charAt(0)).toUpperCase() || '?'
        },
        closeProfile() {
            this.profileOpen = false
        },
        changeLang(code) {
            this.$store.commit('setLang', code)
        },
        doLogout() {
            this.profileOpen = false
            const finalize = () => {
                // Full reload so every Vuex module rehydrates from a cleared
                // localStorage (otherwise impersonation state lingers in memory).
                window.location.href = '/sanctum'
            }
            if (!this.logout) {
                finalize()
                return
            }
            this.logout()
                .then(finalize)
                .catch(finalize)
        }
    }
}
</script>

<style scoped>
.tbv2-wrap {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.tbv2 {
    display: flex;
    align-items: center;
    gap: var(--v2-space-4);
    height: var(--v2-topbar-height, 56px);
    padding: 0 var(--v2-space-5);
    background: var(--v2-surface-0);
    width: 100%;
    box-sizing: border-box;
}

.tbv2-left {
    flex: 1;
    min-width: 0;
    color: var(--v2-text-2);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-medium);
}

.tbv2-search-wrap {
    position: relative;
    width: 280px;
    flex-shrink: 0;
}

.tbv2-search {
    position: relative;
    display: flex;
    align-items: center;
    height: 34px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-1);
    transition: background var(--v2-transition-fast),
        border-color var(--v2-transition-fast),
        box-shadow var(--v2-transition-fast);
}

.tbv2-search:hover {
    border-color: var(--v2-border-strong);
}

.tbv2-search.is-focused {
    background: var(--v2-surface-0);
    border-color: var(--v2-primary);
    box-shadow: var(--v2-shadow-focus);
}

.tbv2-search-icon {
    padding: 0 var(--v2-space-3);
    color: var(--v2-text-4);
    font-size: 0.9rem;
}

.tbv2-search-input {
    flex: 1;
    border: none !important;
    background: transparent !important;
    outline: none !important;
    box-shadow: none !important;
    font-family: var(--v2-font-sans);
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-1);
    height: 100%;
}

.tbv2-search-input:focus {
    outline: none !important;
    box-shadow: none !important;
    border-color: transparent !important;
}

.tbv2-search-input::placeholder {
    color: var(--v2-text-4);
}

.tbv2-search-kbd {
    margin-right: var(--v2-space-3);
    padding: 1px 6px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-xs);
    background: var(--v2-surface-0);
    font-size: 0.7rem;
    color: var(--v2-text-4);
    font-family: var(--v2-font-sans);
}

.tbv2-search-loader {
    margin-right: var(--v2-space-3);
    color: var(--v2-primary);
}

/* Results dropdown */
.tbv2-results {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-shadow-lg);
    padding: var(--v2-space-2);
    max-height: 420px;
    overflow-y: auto;
    z-index: 40;
}

.tbv2-results-group + .tbv2-results-group {
    margin-top: var(--v2-space-2);
    padding-top: var(--v2-space-2);
    border-top: 1px solid var(--v2-border-subtle);
}

.tbv2-results-group-title {
    padding: var(--v2-space-2) var(--v2-space-3) 4px;
    font-size: 0.68rem;
    color: var(--v2-text-4);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: var(--v2-font-weight-semibold);
}

.tbv2-result {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    padding: 6px var(--v2-space-3);
    border-radius: var(--v2-radius-sm);
    color: var(--v2-text-2);
    font-size: var(--v2-text-sm-size);
    cursor: pointer;
    text-decoration: none;
    transition: background var(--v2-transition-fast);
}

.tbv2-result:hover,
.tbv2-result.is-selected {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.tbv2-result-icon {
    color: var(--v2-text-4);
    flex-shrink: 0;
}

.tbv2-result-label {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.tbv2-result-meta {
    font-size: 0.72rem;
    color: var(--v2-text-4);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 40%;
    flex-shrink: 0;
}

.tbv2-results-empty {
    padding: var(--v2-space-4) var(--v2-space-3);
    text-align: center;
    color: var(--v2-text-4);
    font-size: var(--v2-text-sm-size);
}

.tbv2-results-empty i {
    margin-right: var(--v2-space-1);
}

/* Right side */
.tbv2-right {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    flex-shrink: 0;
}

.tbv2-profile {
    position: relative;
}

.tbv2-profile-trigger {
    width: 34px;
    height: 34px;
    border-radius: var(--v2-radius-full);
    border: 1px solid var(--v2-border);
    background: var(--v2-surface-0);
    cursor: pointer;
    padding: 0;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: border-color var(--v2-transition-fast),
        box-shadow var(--v2-transition-fast);
}

.tbv2-profile-trigger:hover {
    border-color: var(--v2-border-strong);
}

.tbv2-profile-trigger:focus-visible {
    outline: none;
    box-shadow: var(--v2-shadow-focus);
}

.tbv2-profile-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.tbv2-profile-avatar {
    font-size: 0.75rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-primary-contrast);
    background: var(--v2-primary);
    width: 100%;
    height: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    letter-spacing: 0.02em;
}

.tbv2-profile-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 240px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-shadow-lg);
    padding: 4px;
    z-index: 50;
}

.tbv2-profile-header {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-3);
    border-radius: var(--v2-radius-md);
}

.tbv2-profile-header-img,
.tbv2-profile-header-avatar {
    width: 36px;
    height: 36px;
    border-radius: var(--v2-radius-full);
    object-fit: cover;
    flex-shrink: 0;
    background: var(--v2-primary);
    color: var(--v2-primary-contrast);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: var(--v2-font-weight-semibold);
    font-size: 0.85rem;
}

.tbv2-profile-header-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.tbv2-profile-header-name {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.tbv2-profile-header-email {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.tbv2-profile-divider {
    height: 1px;
    background: var(--v2-border-subtle);
    margin: 4px 0;
}

.tbv2-profile-item {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    width: 100%;
    padding: 8px var(--v2-space-3);
    border-radius: var(--v2-radius-sm);
    color: var(--v2-text-2);
    font-size: var(--v2-text-sm-size);
    text-decoration: none;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    font-family: var(--v2-font-sans);
    transition: background var(--v2-transition-fast),
        color var(--v2-transition-fast);
}

.tbv2-profile-item:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.tbv2-profile-item--logout {
    color: var(--v2-danger);
}

.tbv2-profile-item--logout:hover {
    background: var(--v2-danger-soft);
    color: var(--v2-danger);
}

.tbv2-profile-item i {
    width: 18px;
    font-size: 0.9rem;
    flex-shrink: 0;
}

.tbv2-profile-lang {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px var(--v2-space-3);
    gap: var(--v2-space-2);
}

.tbv2-profile-lang-label {
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-2);
}

.tbv2-profile-lang-options {
    display: inline-flex;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    overflow: hidden;
}

.tbv2-lang-btn {
    padding: 4px 10px;
    border: none;
    background: transparent;
    color: var(--v2-text-3);
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
    cursor: pointer;
    transition: background var(--v2-transition-fast),
        color var(--v2-transition-fast);
}

.tbv2-lang-btn:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.tbv2-lang-btn.is-active {
    background: var(--v2-primary);
    color: var(--v2-primary-contrast);
}

@media (max-width: 700px) {
    .tbv2-search-wrap { display: none; }
}
</style>
